<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import { RouterItem } from './interfaces/routerItems';
import { useRoute, useRouter } from 'vue-router';
import { removeTokenIfExpired } from '@/appLogic/tokenAroundManagement';
import { displaySessionErrorInfo } from '@/corePagesModule/components/InfoModal/displaySessionErrorInfo';

const t = i18n.t;
const route = useRoute();
const router = useRouter();

const props = withDefaults(
  defineProps<{
    routeItem: RouterItem;
    sideBarIndex: number;
  }>(),
  {}
);

const isOpen = ref(false);

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});

/**
 * サブメニューの表示非表示を切り替える
 */
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

/**
 * ドロップダウンを閉じる(バックグラウンドクリック時)
 * @param e - マウスイベント
 */
const closeDropdown = (e: MouseEvent) => {
  if (!(e.target as Element).closest(`#sidebar-item-${props.sideBarIndex}-sub-menu`)) {
    isOpen.value = false;
  }
};

/**
 * 対象のサイドバー項目が現在のページかどうかを返す処理
 * @param routeItem - 対象のサイドバー項目
 */
const activeSubMenuItem = (routeItem: RouterItem) => {
  // 現在のルートのネスト内に対象のパスが含まれていればアクティブ
  return route.matched.map((route) => route.path).includes(routeItem.to);
};

const handleClick = async (child: RouterItem) => {
  // ここでトークンチェック（Storeや関数を呼ぶ）
  const ok = await removeTokenIfExpired();

  if (ok) {
    router.push(child.to); // 正常ならそのまま遷移
  } else {
    // トークンが期限切れの場合はモーダルを表示してログイン画面に遷移させる
    await displaySessionErrorInfo();
  }
};
</script>

<template>
  <div :id="`sidebar-item-${sideBarIndex}-sub-menu`" class="sidebar-item-with-sub-menu-container">
    <button class="sidebar-item-content" @click="toggleDropdown">
      <div class="sidebar-item-content__icon-wrapper">
        <SvgLoader v-if="routeItem.icon" class="sidebar-item-content__icon-image" :name="routeItem.icon" />
        <span v-if="routeItem.badge && routeItem.badge !== ''" class="sidebar-item-badge">{{ routeItem.badge }}</span>
      </div>
      <i18n-t class="sidebar-item-content__label-text i18n-break" :keypath="routeItem.name" tag="p">
        <template #newLine>
          <br />
        </template>
      </i18n-t>
    </button>
    <div v-if="isOpen" class="sidebar__sub-menu__popup__container">
      <div
        v-for="(child, index) in routeItem.expandChildren"
        :key="index"
        class="sidebar__sub-menu__popup__body"
        :class="{ 'sidebar__sub-menu__popup__body--active': activeSubMenuItem(child) }"
      >
        <router-link
          :data-testid="child.to == '/translate-excel' ? `sidebar-content_inventory_information` : ''"
          :to="child.to"
          @click="toggleDropdown"
          @click.prevent="handleClick(child)"
        >
          <div class="sidebar__sub-menu__popup__body__label">
            {{ t(child.longName ? child.longName : child.name) }}
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-item-with-sub-menu-container {
  position: relative;
}

.sidebar-item-content {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto);
  align-content: space-evenly;
  place-items: center;

  &__icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon-image {
    display: block;
    width: 24px;
  }

  &__label-text {
    width: 100%;
    font: Noto Sans JP;
    font-size: $fontSizeSS;
    font-weight: 700;
    letter-spacing: 0;
    color: $textSecondary;
    white-space: nowrap;
    text-align: center;
  }
}

.sidebar__sub-menu__popup {
  &__container {
    position: absolute;
    top: 0;
    left: 100%;
    width: 140px;
    background-color: $semanticWhite;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 6px;
    z-index: 1000;
    overflow: hidden;
  }

  &__body {
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 40px;

    &--active {
      background-color: $semanticPrimarySubdued;
    }

    &__label {
      display: grid;
      align-items: center;
      width: 100%;
      height: 100%;
      padding-left: 32px;
      color: $textSecondary;
      font-family: Noto Sans JP;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0;
    }
  }
}

.sidebar-item-badge {
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background-color: $semanticError;
  color: $semanticWhite;
  border-radius: 9px;
  font-family: Noto Sans JP;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
</style>
